const DEVANAGARI_REGEX = /[\u0900-\u097F]/;

const VOWELS = [
  ['aa', { independent: '\u0906', matra: '\u093e' }],
  ['ai', { independent: '\u0910', matra: '\u0948' }],
  ['au', { independent: '\u0914', matra: '\u094c' }],
  ['ee', { independent: '\u0908', matra: '\u0940' }],
  ['ii', { independent: '\u0908', matra: '\u0940' }],
  ['oo', { independent: '\u090a', matra: '\u0942' }],
  ['uu', { independent: '\u090a', matra: '\u0942' }],
  ['ri', { independent: '\u090b', matra: '\u0943' }],
  ['a', { independent: '\u0905', matra: '' }],
  ['i', { independent: '\u0907', matra: '\u093f' }],
  ['u', { independent: '\u0909', matra: '\u0941' }],
  ['e', { independent: '\u090f', matra: '\u0947' }],
  ['o', { independent: '\u0913', matra: '\u094b' }]
];

const CONSONANTS = [
  ['chh', '\u091b'],
  ['tth', '\u0920'],
  ['ddh', '\u0922'],
  ['kh', '\u0916'],
  ['gh', '\u0918'],
  ['ch', '\u091a'],
  ['jh', '\u091d'],
  ['tt', '\u091f'],
  ['dd', '\u0921'],
  ['th', '\u0925'],
  ['dh', '\u0927'],
  ['ph', '\u092b'],
  ['bh', '\u092d'],
  ['sh', '\u0936'],
  ['gn', '\u091c\u094d\u091e'],
  ['tr', '\u0924\u094d\u0930'],
  ['gy', '\u091c\u094d\u091e'],
  ['k', '\u0915'],
  ['g', '\u0917'],
  ['c', '\u0915'],
  ['j', '\u091c'],
  ['t', '\u0924'],
  ['d', '\u0926'],
  ['n', '\u0928'],
  ['p', '\u092a'],
  ['b', '\u092c'],
  ['m', '\u092e'],
  ['y', '\u092f'],
  ['r', '\u0930'],
  ['l', '\u0932'],
  ['v', '\u0935'],
  ['w', '\u0935'],
  ['s', '\u0938'],
  ['h', '\u0939'],
  ['f', '\u092b'],
  ['z', '\u091c\u093c'],
  ['q', '\u0915'],
  ['x', '\u0915\u094d\u0938']
];

const CP1252_REVERSE = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99,
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f
};

// Pronunciation exceptions for common names/surnames where generic roman->Hindi rules fail.
const NAME_OVERRIDES_HI = {
  ritika: '\u0930\u093f\u0924\u093f\u0915\u093e',
  syal: '\u0938\u094d\u092f\u093e\u0932'
};

// Heuristics for common Indian-name endings where plain roman text misses long "aa".
const ENDING_HEURISTICS = [
  [/ika$/i, 'ikaa'], // Ritika -> Ritikaa
  [/ita$/i, 'itaa'], // Anita -> Anitaa
  [/ina$/i, 'inaa'], // Karina -> Karinaa
  [/yal$/i, 'yaal']  // Syal -> Syaal
];

function toCp1252Bytes(input) {
  const bytes = [];

  for (const char of input) {
    const cp = char.codePointAt(0);
    if (cp <= 0xff) {
      bytes.push(cp);
      continue;
    }

    const mapped = CP1252_REVERSE[cp];
    if (mapped === undefined) return null;
    bytes.push(mapped);
  }

  return Uint8Array.from(bytes);
}

function repairMojibake(value) {
  if (typeof value !== 'string' || !value) return value;

  let current = value;
  for (let i = 0; i < 4; i += 1) {
    const bytes = toCp1252Bytes(current);
    if (!bytes) break;

    const decoded = new TextDecoder('utf-8').decode(bytes);
    if (!decoded || decoded === current) break;
    current = decoded;
  }

  return current;
}

function matchPattern(input, start, patterns) {
  for (let i = 0; i < patterns.length; i += 1) {
    const [key, value] = patterns[i];
    if (input.startsWith(key, start)) {
      return { key, value };
    }
  }
  return null;
}

function isVowelAhead(input, index) {
  return Boolean(matchPattern(input, index, VOWELS));
}

function romanWordToHindi(word) {
  const lower = word.toLowerCase();
  let output = '';
  let i = 0;

  while (i < lower.length) {
    const consonant = matchPattern(lower, i, CONSONANTS);
    if (consonant) {
      i += consonant.key.length;
      const vowel = matchPattern(lower, i, VOWELS);
      if (vowel) {
        output += consonant.value + vowel.value.matra;
        i += vowel.key.length;
      } else if (i < lower.length && /[a-z]/.test(lower[i]) && !isVowelAhead(lower, i)) {
        output += consonant.value + '\u094d';
      } else {
        output += consonant.value;
      }
      continue;
    }

    const vowel = matchPattern(lower, i, VOWELS);
    if (vowel) {
      output += vowel.value.independent;
      i += vowel.key.length;
      continue;
    }

    output += word[i];
    i += 1;
  }

  return output
    .replace(/\u0928\u094d([\u091c\u091d\u091e])/g, '\u0902$1')
    .replace(/\u0928\u094d([\u0915\u0916\u0917\u0918])/g, '\u0902$1')
    .replace(/\u0928\u094d([\u091f\u0920\u0921\u0922])/g, '\u0902$1')
    .replace(/\u0928\u094d([\u0924\u0925\u0926\u0927])/g, '\u0902$1')
    .replace(/\u0928\u094d([\u092a\u092b\u092c\u092d])/g, '\u0902$1');
}

function normalizeRomanNameWord(word) {
  let normalized = word;
  ENDING_HEURISTICS.forEach(([pattern, replacement]) => {
    normalized = normalized.replace(pattern, replacement);
  });
  return normalized;
}

export function getLocalizedName(name, language) {
  const repaired = repairMojibake(String(name || '').trim());
  if (!repaired || language !== 'hi' || DEVANAGARI_REGEX.test(repaired)) {
    return repaired;
  }

  return repaired
    .split(/(\s+|-|')/)
    .map((part) => {
      if (!/^[A-Za-z]+$/.test(part)) return part;
      const override = NAME_OVERRIDES_HI[part.toLowerCase()];
      return override || romanWordToHindi(normalizeRomanNameWord(part));
    })
    .join('');
}
